import { lazy, Suspense as ReactSuspense } from "react"
import { Navigate, RouteObject, useParams } from "react-router-dom"
import { createHashRouter } from "react-router-dom"
import { Box } from "@chakra-ui/react"

import Status from "components/Status"

const Layout = lazy(() => import("components/Layout"))
const Login = lazy(() => import("pages/Login"))
const NotFound = lazy(() => import("pages/NotFound"))
const TournamentList = lazy(() => import("pages/DataSources/TournamentList"))
const TournamentDetail = lazy(
  () => import("pages/DataSources/TournamentList/pages/TournamentDetail"),
)
const TournamentAudit = lazy(() => import("pages/DataSources/TournamentList/pages/TournamentAudit"))
const CompetitorList = lazy(() => import("pages/DataSources/CompetitorList"))
const CompetitorDetail = lazy(
  () => import("pages/DataSources/CompetitorList/pages/CompetitorDetail"),
)
const CompetitorAudit = lazy(() => import("pages/DataSources/CompetitorList/pages/CompetitorAudit"))
const CategoryList = lazy(() => import("pages/DataSources/CategoryList"))
const CategoryDetail = lazy(() => import("pages/DataSources/CategoryList/pages/CategoryDetail"))
const CategoryAudit = lazy(() => import("pages/DataSources/CategoryList/pages/CategoryAudit"))
const PlayerList = lazy(() => import("pages/DataSources/PlayerList"))
const PlayerAudit = lazy(() => import("pages/DataSources/PlayerList/pages/PlayerAudit"))
const PlayerDetail = lazy(() => import("pages/DataSources/PlayerList/pages/PlayerDetail"))
const ChampionshipMarketsList = lazy(() => import("pages/DataSources/ChampionshipMarketsList"))
const ChampionshipMarketsDetail = lazy(
  () => import("pages/DataSources/ChampionshipMarketsList/pages/ChampionshipMarketsDetail"),
)
const ChampionshipOutcomeTranslation = lazy(
  () => import("pages/TranslationWarning/ChampionshipOutcome"),
)
const ChampionshipOutcomeTranslationAudit = lazy(
  () => import("pages/TranslationWarning/ChampionshipOutcome/ChampionshipOutcomeAudit"),
)
const ChampionshipMarketsAudit = lazy(
  () => import("pages/DataSources/ChampionshipMarketsList/pages/ChampionshipMarketsAudit"),
)
const ChampionshipOutcomesList = lazy(() => import("pages/DataSources/ChampionshipOutcomesList"))
const ChampionshipOutcomesDetail = lazy(
  () => import("pages/DataSources/ChampionshipOutcomesList/pages/ChampionshipOutcomesDetail"),
)
const ChampionshipOutcomesAudit = lazy(
  () => import("pages/DataSources/ChampionshipOutcomesList/pages/ChampionshipOutcomesAudit"),
)
const TournamentSort = lazy(() => import("pages/DataSources/TournamentSort"))
const ManualResult = lazy(() => import("pages/MarketOdds/ManualResult"))
const ChampionshipManualResult = lazy(() => import("pages/MarketOdds/ChampionshipManualResult"))
const ManualResultChampion = lazy(() => import("pages/MarketOdds/ManualResultChampion"))
const ResultManagement = lazy(() => import("pages/MarketOdds/ResultManagement"))
const BetOrder = lazy(() => import("pages/MarketOdds/BetOrder"))
const BetOrderActions = lazy(() => import("pages/MarketOdds/BatchOrderActions"))
const MatchReplay = lazy(() => import("pages/MarketOdds/MatchReplay"))
const OddsAnalysis = lazy(() => import("pages/MarketOdds/OddsAnalysis"))
const MatchMergerList = lazy(() => import("pages/MatchMerge/MatchMergerList"))
const AbnormalMatchList = lazy(() => import("pages/MatchMerge/AbnormalMatchList"))
const MatchAnnouncement = lazy(() => import("pages/MatchSetting/MatchAnnouncement"))
const MatchMarketList = lazy(() => import("pages/MatchSetting/MatchMarketList"))
const ChampionshipMarketDisplay = lazy(() => import("pages/MatchSetting/ChampionshipMarketDisplay"))
const MatchEventPriority = lazy(() => import("pages/MatchSetting/MatchEventPriority"))
const MatchSetting = lazy(() => import("pages/MatchSetting/MatchSetting"))
const TournamentBlackList = lazy(() => import("pages/MatchSetting/TournamentBlackList"))
const TournamentTranslation = lazy(() => import("pages/TranslationWarning/Tournament"))
const CompetitorTranslation = lazy(() => import("pages/TranslationWarning/Competitor"))
const PlayerTranslation = lazy(() => import("pages/TranslationWarning/Player"))
const MarketOverview = lazy(() => import("pages/MarketOdds/MarketOverview"))
const AuthCallback = lazy(() => import("pages/AuthCallback"))
const AuthManagement = lazy(() => import("pages/AuthManagement"))
const RolePermissions = lazy(() => import("pages/AuthManagement/RoleList/RolePermissions"))
const PendingAccess = lazy(() => import("pages/PendingAccess"))

export type HandleType = {
  /** 麵包屑 */
  crumb?: {
    /** 頁面權限代號 */
    code?: string
    /** 頁面名稱 */
    name: string
    /** 頁面連結 */
    path: string
    /** 頁面描述 */
    description?: React.ReactNode
  }
}

const ConditionalRoute = ({ tab, children }: { tab: string; children: React.ReactNode }) => {
  const { tab: currentTab } = useParams() // 抓取當前的 tab 值

  // 如果 tab 不符合要求，跳轉到父路徑或顯示 404
  if (currentTab !== tab) {
    return <Navigate to="/auth" replace />
  }

  // 如果 tab 匹配，渲染子組件
  return children
}

const routes: RouteObject[] = [
  {
    path: "/login",
    element: (
      <ReactSuspense fallback={<Status.Spinner />}>
        <Login />
      </ReactSuspense>
    ),
    errorElement: <NotFound />,
  },
  {
    path: "/auth/callback",
    element: (
      <ReactSuspense fallback={<Status.Spinner />}>
        <AuthCallback />
      </ReactSuspense>
    ),
  },
  {
    path: "/pending-access",
    element: (
      <ReactSuspense fallback={<Status.Spinner />}>
        <PendingAccess />
      </ReactSuspense>
    ),
  },
  {
    path: "/",
    element: <Layout />,
    errorElement: <NotFound />,
    children: [
      {
        path: "/tournament-list",
        handle: {
          crumb: {
            name: "聯賽數據",
            path: "/tournament-list",
          },
        },
        children: [
          {
            index: true,
            element: <TournamentList />,
          },
          {
            path: "/tournament-list/create",
            element: <TournamentDetail />,
            handle: {
              crumb: {
                name: "新增聯賽",
                path: "/tournament-list/create",
              },
            },
          },
          {
            path: "/tournament-list/edit/:tournamentId",
            element: <TournamentDetail />,
            handle: {
              crumb: {
                name: "編輯聯賽",
                path: "/tournament-list/edit/:tournamentId",
              },
            },
          },
          {
            path: "/tournament-list/audit/:tournamentId",
            element: <TournamentAudit />,
            handle: {
              crumb: {
                name: "聯賽歷程",
                path: "/tournament-list/audit/:tournamentId",
              },
            },
          },
        ],
      },
      {
        path: "/competitor-list",
        handle: {
          crumb: {
            name: "球隊數據",
            path: "/competitor-list",
          },
        },
        children: [
          {
            index: true,
            element: <CompetitorList />,
          },
          {
            path: "/competitor-list/create",
            element: <CompetitorDetail />,
            handle: {
              crumb: {
                name: "新增球隊",
                path: "/competitor-list/create",
              },
            },
          },
          {
            path: "/competitor-list/edit/:competitorId",
            element: <CompetitorDetail />,
            handle: {
              crumb: {
                name: "編輯球隊",
                path: "/competitor-list/edit/:competitorId",
              },
            },
          },
          {
            path: "/competitor-list/audit/:competitorId",
            element: <CompetitorAudit />,
            handle: {
              crumb: {
                name: "球隊歷程",
                path: "/competitor-list/audit/:competitorId",
              },
            },
          },
        ],
      },
      {
        path: "/category-list",
        handle: {
          crumb: {
            name: "區域數據",
            path: "/category-list",
          },
        },
        children: [
          {
            index: true,
            element: <CategoryList />,
          },
          {
            path: "/category-list/create",
            element: <CategoryDetail />,
            handle: {
              crumb: {
                name: "新增區域",
                path: "/category-list/create",
              },
            },
          },
          {
            path: "/category-list/edit/:categoryId",
            element: <CategoryDetail />,
            handle: {
              crumb: {
                name: "編輯區域",
                path: "/category-list/edit/:categoryId",
              },
            },
          },
          {
            path: "/category-list/audit/:categoryId",
            element: <CategoryAudit />,
            handle: {
              crumb: {
                name: "區域歷程",
                path: "/category-list/audit/:categoryId",
              },
            },
          },
        ],
      },
      {
        path: "/player-list",
        handle: {
          crumb: {
            name: "球員數據",
            path: "/player-list",
          },
        },
        children: [
          {
            index: true,
            element: <PlayerList />,
          },
          {
            path: "/player-list/create",
            element: <PlayerDetail />,
            handle: {
              crumb: {
                name: "新增球員",
                path: "/player-list/create",
              },
            },
          },
          {
            path: "/player-list/edit/:playerId",
            element: <PlayerDetail />,
            handle: {
              crumb: {
                name: "編輯球員",
                path: "/player-list/edit/:playerId",
              },
            },
          },
          {
            path: "/player-list/audit/:playerId",
            element: <PlayerAudit />,
            handle: {
              crumb: {
                name: "球員歷程",
                path: "/player-list/audit/:playerId",
              },
            },
          },
        ],
      },
      {
        path: "/championship-list/markets",
        handle: {
          crumb: {
            name: "冠軍盤口數據",
            path: "/championship-list/markets",
          },
        },
        children: [
          {
            index: true,
            element: <ChampionshipMarketsList />,
          },
          {
            path: "/championship-list/markets/edit/:marketId",
            element: <ChampionshipMarketsDetail />,
            handle: {
              crumb: {
                name: "編輯冠軍盤口",
                path: "/championship-list/markets/edit/:marketId",
              },
            },
          },
          {
            path: "/championship-list/markets/audit/:marketId",
            element: <ChampionshipMarketsAudit />,
            handle: {
              crumb: {
                name: "冠軍盤口歷程",
                path: "/championship-list/markets/audit/:marketId",
              },
            },
          },
        ],
      },
      {
        path: "/championship-list/outcomes",
        handle: {
          crumb: {
            name: "冠軍投注項數據",
            path: "/championship-list/outcomes",
          },
        },
        children: [
          {
            index: true,
            element: <ChampionshipOutcomesList />,
          },
          {
            path: "/championship-list/outcomes/edit/:outcomeId",
            element: <ChampionshipOutcomesDetail />,
            handle: {
              crumb: {
                name: "編輯冠軍投注項",
                path: "/championship-list/outcomes/edit/:outcomeId",
              },
            },
          },
          {
            path: "/championship-list/outcomes/audit/:outcomeId",
            element: <ChampionshipOutcomesAudit />,
            handle: {
              crumb: {
                name: "冠軍投注項歷程",
                path: "/championship-list/outcomes/audit/:outcomeId",
              },
            },
          },
        ],
      },
      {
        path: "/championship-list/outcome-translation",
        handle: {
          crumb: {
            name: "冠軍投注項多語系翻譯",
            path: "/championship-list/outcome-translation",
          },
        },
        children: [
          {
            index: true,
            element: <ChampionshipOutcomeTranslation />,
          },
          {
            path: "/championship-list/outcome-translation/audit/:id",
            element: <ChampionshipOutcomeTranslationAudit />,
            handle: {
              crumb: {
                name: "投注項歷程",
                path: "/championship-list/outcome-translation/audit/:id",
              },
            },
          },
        ],
      },
      {
        path: "/tournament-sort",
        handle: {
          crumb: {
            name: "聯賽排序",
            path: "/tournament-sort",
          },
        },
        children: [
          {
            index: true,
            element: <TournamentSort />,
          },
        ],
      },
      // 顯示盤口/賠率/結算
      {
        path: "/manual-result",
        handle: {
          crumb: {
            name: "手動賽果",
            path: "/manual-result",
          },
        },
        children: [
          {
            index: true,
            element: <ManualResult />,
          },
        ],
      },
      {
        path: "/championship-manual-result",
        handle: {
          crumb: {
            name: "手動賽果（B冠軍）",
            path: "/championship-manual-result",
          },
        },
        children: [
          {
            index: true,
            element: <ChampionshipManualResult />,
          },
        ],
      },
      // TODO: 待過度完後，移除
      {
        path: "/manual-result-champion",
        handle: {
          crumb: {
            name: "手動賽果（A冠軍）",
            path: "/manual-result-champion",
          },
        },
        children: [
          {
            index: true,
            element: <ManualResultChampion />,
          },
        ],
      },
      {
        path: "/bet-order",
        handle: {
          crumb: {
            name: "注單查詢",
            path: "/bet-order",
          },
        },
        children: [
          {
            index: true,
            element: <BetOrder />,
          },
        ],
      },
      {
        path: "/odds-analysis",
        handle: {
          crumb: {
            name: "賽事賠率歷程",
            path: "/odds-analysis",
          },
        },
        children: [
          {
            index: true,
            element: <OddsAnalysis />,
          },
        ],
      },
      {
        path: "/result-management",
        handle: {
          crumb: {
            name: "賽果管理",
            path: "/result-management",
          },
        },
        children: [
          {
            index: true,
            element: <ResultManagement />,
          },
        ],
      },
      {
        path: "/market-beton-overview/:tab?",
        handle: {
          crumb: {
            name: "盤口管理",
            path: "/market-beton-overview",
          },
        },
        children: [
          {
            index: true,
            element: <MarketOverview />,
          },
        ],
      },
      {
        path: "/batch-order-actions",
        handle: {
          crumb: {
            name: "注單批次處理",
            path: "/batch-order-actions",
          },
        },
        children: [
          {
            index: true,
            element: <BetOrderActions />,
          },
        ],
      },
      {
        path: "/match-replay",
        handle: {
          crumb: {
            name: "賽事回放",
            path: "/match-replay",
          },
        },
        children: [
          {
            index: true,
            element: <MatchReplay />,
          },
        ],
      },
      // 賽事整合
      {
        path: "/match-merger-list",
        handle: {
          crumb: {
            name: "賽事整合",
            path: "/match-merger-list",
            description: (
              <Box>
                自動整合賽事條件為「聯賽 ID + 主客隊 ID
                相同，數據源開賽時間差小於兩小時」，當前頁面為手動整合賽事，可被整合賽事顯示條件為「聯賽
                ID 相同下，主對主、客對客或主對客、客對主其一 ID 相同」
              </Box>
            ),
          },
        },
        children: [
          {
            index: true,
            element: <MatchMergerList />,
          },
        ],
      },
      {
        path: "/abnormal-match-list",
        handle: {
          crumb: {
            name: "異常賽事",
            path: "/abnormal-match-list",
          },
        },
        children: [
          {
            index: true,
            element: <AbnormalMatchList />,
          },
        ],
      },
      // 聯賽/賽事設定
      {
        path: "/match-announcement",
        handle: {
          crumb: {
            name: "賽事公告",
            path: "/match-announcement",
          },
        },
        children: [
          {
            index: true,
            element: <MatchAnnouncement />,
          },
        ],
      },
      {
        path: "/match-market-list",
        handle: {
          crumb: {
            name: "前台賽事數據",
            path: "/match-market-list",
          },
        },
        children: [
          {
            index: true,
            element: <MatchMarketList />,
          },
        ],
      },
      {
        path: "/championship-market-display",
        handle: {
          crumb: {
            name: "冠軍盤前台數據",
            path: "/championship-market-display",
          },
        },
        children: [
          {
            index: true,
            element: <ChampionshipMarketDisplay />,
          },
        ],
      },
      {
        path: "/event-source-priority",
        handle: {
          crumb: {
            name: "事件數據源優先序",
            path: "/event-source-priority",
          },
        },
        children: [
          {
            index: true,
            element: <MatchEventPriority />,
          },
        ],
      },
      {
        path: "/match-setting",
        handle: {
          crumb: {
            name: "賽事設定變更",
            path: "/match-setting",
          },
        },
        children: [
          {
            index: true,
            element: <MatchSetting />,
          },
        ],
      },
      {
        path: "/tournament-white-list",
        handle: {
          crumb: {
            name: "聯賽黑名單",
            path: "/tournament-white-list",
          },
        },
        children: [
          {
            index: true,
            element: <TournamentBlackList />,
          },
        ],
      },
      // 翻譯告警
      {
        path: "/beta/translation/tournament",
        handle: {
          crumb: {
            name: "聯賽多語系翻譯",
            path: "/beta/translation/tournament",
          },
        },
        children: [
          {
            index: true,
            element: <TournamentTranslation />,
          },
        ],
      },
      {
        path: "/translation/competitor",
        handle: {
          crumb: {
            name: "球隊多語系翻譯",
            path: "/translation/competitor",
          },
        },
        children: [
          {
            index: true,
            element: <CompetitorTranslation />,
          },
        ],
      },
      {
        path: "/beta/translation/player",
        handle: {
          crumb: {
            name: "球員多語系翻譯",
            path: "/beta/translation/player",
          },
        },
        children: [
          {
            index: true,
            element: <PlayerTranslation />,
          },
        ],
      },

      {
        path: "/auth/:tab?",
        handle: {
          crumb: {
            name: "權限管理",
            path: "/auth",
          },
        },
        children: [
          {
            index: true,
            element: <AuthManagement />,
          },
          {
            path: ":roleId/permissions",
            element: (
              <ConditionalRoute tab="roles">
                <RolePermissions />
              </ConditionalRoute>
            ),
            handle: {
              crumb: {
                name: "編輯角色",
                path: "/auth/roles/:roleId/permissions",
              },
            },
          },
        ],
      },

      {
        path: "/404",
        element: <NotFound />,
      },
    ],
  },
  /* 404 not found */
  {
    path: "*",
    element: <NotFound />,
  },
]

const router = createHashRouter(routes, {
  future: {
    v7_relativeSplatPath: true,
  },
})
export default router
export { routes }
